/**
 * Bidirectional URL <-> selection sync for the product editor (2026-07-15).
 *
 * Makes each product-editor tab AND each selected placement a bookmarkable,
 * refresh-stable URL, consistent with the project's "real paths, NO #hash"
 * rule and createWebHistory().
 *
 *   /distributor/product-editor/:campaignProductId/:tab
 *   /distributor/product-editor/:campaignProductId/placements/:placementId
 *   /distributor/product-editor/:campaignProductId            (bare -> redirect)
 *
 * The editor stays a SINGLE page that swaps its ContextPanel internally by
 * `useProductEditorUiState().selectedLeftRailItem`. This composable is the
 * two-way bridge between that store selection and the URL:
 *
 *   route -> store : a watcher reads :tab / :placementId, maps slug->key, and
 *                    sets selectedLeftRailItem. An invalid tab slug
 *                    redirects to the last-viewed (else first) tab so URL
 *                    and view never disagree. A placement id that is gone
 *                    (deleted, or a dead deep link) lands on a REMAINING
 *                    placement -- the deleted row's rail neighbor, else the
 *                    first -- and only falls back to the tab redirect when
 *                    the product has no placements left.
 *   store -> route : a watcher pushes/replaces the matching URL when the
 *                    selection changes from a user click (LeftRail onSelect /
 *                    onSelectPlacement).
 *
 * Infinite-loop guard: applying the route to the store sets a `syncingFromRoute`
 * flag so the store->route watcher skips the redundant navigation it would
 * otherwise trigger; both watchers additionally no-op when the route already
 * matches the desired target. The initial route->store application uses
 * router.replace (no history spam); user-driven selection changes use push.
 */
import { watch } from "vue";
import {
    slugToKey,
    keyToSlug,
    isValidTabSlug,
    placementIdFromKey,
    placementKeyFromId,
    persistLastTabSlug,
    readLastTabSlug,
    FIRST_TAB_SLUG,
} from "./productEditorTabRoutes";

// Build the tab URL for a campaign product + slug.
function tabPath(campaignProductId, slug) {
    return `/distributor/product-editor/${campaignProductId}/${slug}`;
}

// Build the placement URL for a campaign product + placement id.
function placementPath(campaignProductId, placementId) {
    return `/distributor/product-editor/${campaignProductId}/placements/${placementId}`;
}

/**
 * Wire the sync. Call once from ProductEditor.vue setup().
 *
 * @param {object} deps
 * @param {import('vue-router').RouteLocationNormalizedLoaded} deps.route
 * @param {import('vue-router').Router} deps.router
 * @param {ReturnType<typeof import('@/stores/productEditorUiState').useProductEditorUiState>} deps.editorUi
 * @param {ReturnType<typeof import('@/stores/productEditor').useProductEditorStore>} deps.store
 */
export function useProductEditorRouteSync({ route, router, editorUi, store }) {
    // True while we are pushing the store selection FROM the route, so the
    // store->route watcher does not fire a redundant navigation (the classic
    // route<->state ping-pong). Reset on the next tick after the store write.
    let syncingFromRoute = false;

    const campaignProductId = () => route.params.campaignProductId;

    // Does the current route already point at this tab slug (no placement)?
    function routeMatchesTab(slug) {
        return route.params.tab === slug && route.params.placementId == null;
    }

    // Does the current route already point at this placement id?
    function routeMatchesPlacement(placementId) {
        return (
            route.params.placementId != null &&
            String(route.params.placementId) === String(placementId)
        );
    }

    // Whether a placement id from the URL corresponds to a real, loaded
    // decoration. Decorations load async; until they arrive (empty array) we
    // cannot say the id is invalid, so we defer the redirect and select
    // optimistically -- a later decorations change re-runs this check.
    // The placement list is authoritative once the product's server state has
    // settled -- an EMPTY list then means "no placements", not "not loaded
    // yet". Deleting the selected last placement depends on that: treating
    // empty as unloaded deferred the redirect forever and left the URL on
    // the deleted placement, across reloads too. A non-empty list is
    // evidence of a load on its own.
    function placementsLoaded() {
        if ((store.decorations ?? []).length > 0) return true;
        return store.serverState != null && !store.loading;
    }

    function placementIsLoadedAndMissing(placementId) {
        const decorations = store.decorations ?? [];
        if (!placementsLoaded()) return false; // not loaded yet -> defer
        return !decorations.some(
            (d) => String(d.id ?? d._tempId) === String(placementId),
        );
    }

    // Resolve a route placementId (always a STRING) to the matching
    // decoration's REAL id, preserving its type (server ids are numbers,
    // pending ids are `tmp-*` strings). This matters because the store's
    // selectedPlacement computed matches decorations by strict `===`
    // (`(d.id ?? d._tempId) === selectedPlacementId`); selecting the raw
    // string "12" would never match numeric id 12 and the Stage/inspector
    // would render nothing. When decorations are not loaded yet, fall back
    // to the raw string id (optimistic -- the decorations watcher re-runs
    // once they load and re-resolves to the typed id).
    function resolvePlacementId(routePlacementId) {
        const decorations = store.decorations ?? [];
        const match = decorations.find(
            (d) => String(d.id ?? d._tempId) === String(routePlacementId),
        );
        return match ? (match.id ?? match._tempId) : routePlacementId;
    }

    // Redirect a bare / invalid URL to the last-viewed (else first) tab. Uses
    // replace so a bad URL does not leave a dead entry in history.
    function redirectToLastOrFirstTab() {
        const slug = readLastTabSlug() || FIRST_TAB_SLUG;
        router.replace(tabPath(campaignProductId(), slug));
    }

    // Placement ids in RAIL order -- the rail renders store.decorations as-is,
    // so this is the order the user sees.
    function placementIdsInRailOrder() {
        return (store.decorations ?? []).map((d) => d.id ?? d._tempId);
    }

    // The list as of the PREVIOUS decorations change. A delete prunes the
    // row before this composable sees it, so the deleted placement's rail
    // position survives only here.
    let previousPlacementIds = placementIdsInRailOrder();

    // The landing chosen for a gone placement id, held until the URL leaves
    // it. router.replace settles asynchronously, and a later list change
    // (the post-delete refresh) would otherwise re-pick against a list that
    // no longer remembers where the deleted row was.
    let pendingLanding = null;

    // Which placement to land on when the URL names one that is gone: the
    // placement that FOLLOWED it in rail order, else the one before it, else
    // (a dead deep link with no remembered position) the first. null when
    // the product has no placements left.
    function landingPlacementFor(goneId) {
        const ids = placementIdsInRailOrder();
        if (ids.length === 0) return null;
        const remains = (id) => ids.some((x) => String(x) === String(id));
        if (pendingLanding?.from === String(goneId) && remains(pendingLanding.to)) {
            return pendingLanding.to;
        }
        const at = previousPlacementIds.findIndex(
            (id) => String(id) === String(goneId),
        );
        let to = ids[0];
        if (at !== -1) {
            const after = previousPlacementIds.slice(at + 1).find(remains);
            const before = previousPlacementIds.slice(0, at).reverse().find(remains);
            to = after ?? before ?? ids[0];
        }
        pendingLanding = { from: String(goneId), to };
        return to;
    }

    // ROUTE -> STORE. Reads the current route params and drives the store
    // selection. `initial` uses replace when it must correct the URL.
    function applyRouteToStore() {
        const { tab, placementId } = route.params;

        // Placement URL: /placements/:id
        if (placementId != null) {
            if (placementIsLoadedAndMissing(placementId)) {
                // Gone placement -> land on a remaining one; with none left,
                // treat it like a bare/invalid URL.
                const landing = landingPlacementFor(placementId);
                if (landing == null) redirectToLastOrFirstTab();
                else router.replace(placementPath(campaignProductId(), landing));
                return;
            }
            // Match against the loaded decoration's real (typed) id so the
            // store's strict-equality selectedPlacement lookup resolves. Use
            // a STRICT compare so an optimistic string id ("12", selected
            // before decorations loaded) is upgraded to the numeric id (12)
            // once the decoration arrives -- otherwise the store's
            // selectedPlacement lookup (strict `===`) never matches.
            const resolvedId = resolvePlacementId(placementId);
            if (editorUi.selectedPlacementId !== resolvedId) {
                syncingFromRoute = true;
                editorUi.setSelectedPlacement(resolvedId);
                syncingFromRoute = false;
            }
            return;
        }

        // Bare id (no tab) or an invalid tab slug -> redirect to last/first.
        if (tab == null || !isValidTabSlug(tab)) {
            redirectToLastOrFirstTab();
            return;
        }

        // Valid tab slug -> set the matching rail key + remember it.
        const key = slugToKey(tab);
        persistLastTabSlug(tab);
        if (editorUi.selectedLeftRailItem !== key) {
            syncingFromRoute = true;
            editorUi.setLeftRailItem(key);
            syncingFromRoute = false;
        }
    }

    // STORE -> ROUTE. When the selection changes from a user click, push the
    // matching URL (unless we are mid route->store apply, or the route already
    // matches -- both guard the loop).
    function applyStoreToRoute(selection) {
        if (syncingFromRoute) return;

        const placementId = placementIdFromKey(selection);
        if (placementId != null) {
            if (routeMatchesPlacement(placementId)) return;
            router.push(placementPath(campaignProductId(), placementId));
            return;
        }

        const slug = keyToSlug(selection);
        if (slug == null) return; // unknown selection -> leave URL alone
        persistLastTabSlug(slug);
        if (routeMatchesTab(slug)) return;
        router.push(tabPath(campaignProductId(), slug));
    }

    // Re-run route->store whenever the tab or placement param changes
    // (immediate: seeds the initial selection / redirect on mount).
    watch(
        () => [route.params.tab, route.params.placementId],
        () => applyRouteToStore(),
        { immediate: true },
    );

    // Decorations arrive async: once they load, re-validate a deep-linked
    // placement URL (redirects if the id turned out not to exist), and a
    // delete re-validates the selected one. Keyed on the ids, not the count,
    // so a list that loses one row and gains another still re-validates; and
    // on placementsLoaded() so a load that settles with ZERO placements --
    // the list never changes -- still re-validates. The previous list is
    // recorded AFTER the re-validation, which needs the deleted row's place.
    watch(
        () => [placementIdsInRailOrder().map(String).join(","), placementsLoaded()],
        () => {
            if (route.params.placementId != null) applyRouteToStore();
            previousPlacementIds = placementIdsInRailOrder();
        },
    );

    // Store selection changes (LeftRail clicks) drive the URL.
    watch(
        () => editorUi.selectedLeftRailItem,
        (selection) => applyStoreToRoute(selection),
    );
}
